/**
 * @copyright (C) 2024, Rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of Rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by Rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

const LOGTAG = "#SoundManager#";

class SoundManager {
	#soundList: Map<string, HTMLAudioElement> = new Map();

	public install(soundName: string, audioEl: HTMLAudioElement) {
		if (this.#soundList.has(soundName)) {
			throw Error("sound already exists");
		}
		this.#soundList.set(soundName, audioEl);
		console.debug(LOGTAG, "SoundManager install", soundName);
		if (audioEl) {
			// @ts-ignore
			audioEl.onerror = (event: unknown) => {
				console.debug(LOGTAG, "SoundManager Error with", event);
			};
		}
	}

	public async play(soundName: string): Promise<void>;
	public async play(soundName: string, audioEl: HTMLAudioElement): Promise<void>;
	public async play(soundName: string, audioEl?: HTMLAudioElement): Promise<void>;
	public async play(soundName: string, audioEl?: HTMLAudioElement, playTime?: number): Promise<void>;
	public async play(soundName: string, audioEl?: HTMLAudioElement, playTime?: number): Promise<void> {
		const sound = this.find(soundName);
		let playPromise: Promise<void> | null = null;
		if (sound) {
			if (playTime) {
				if (sound.duration < playTime) {
					sound.setAttribute("loop", "true");
				}
				playPromise = sound.play();
			} else {
				playPromise = sound.play();
			}
		} else {
			if (!audioEl) throw Error("audioElement not found");
			try {
				this.install(soundName, audioEl);
			} catch (error) {
				console.warn(LOGTAG, "SoundManager exception", error);
				return;
			}
			if (playTime) {
				if (audioEl.duration < playTime) {
					audioEl.setAttribute("loop", "true");
				}
				playPromise = audioEl.play();
			} else {
				playPromise = audioEl.play();
			}
		}
		if (playPromise) {
			playPromise
				.then(() => {})
				.catch((error) => {
					console.warn("#SoundManager# error", error);
				});
		}
	}

	public find(soundName: string) {
		return this.#soundList.get(soundName);
	}

	public pause(soundName: string) {
		const sound = this.#soundList.get(soundName);

		if (sound) {
			sound.pause();
		}
	}
	public stop(soundName: string) {
		const sound = this.#soundList.get(soundName);

		if (sound) {
			sound.pause();
			sound.currentTime = 0;
		}
	}
}

const singletonInstance = Object.freeze(new SoundManager());
export default singletonInstance;
